/**
 * The admin-facing discount note (COL-360).
 *
 * One component so every admin surface says the same thing in the same words:
 * what the discount is, which months it covers, what the rent goes back to, and
 * why it was given. Rent figures appear in a dozen admin views; without a
 * shared renderer each one would invent its own phrasing, and the reason would
 * show up in some and not others.
 *
 * ADMIN SURFACES ONLY. The reason is an internal justification — it is never
 * rendered in the owner portal or the tenant portal, and never sent in email.
 * The public listing shows the room's base price and knows nothing about this.
 */
import { BadgePercent } from "lucide-react";
import {
  formatDiscountSummary,
  hasDepositDiscount,
  hasRentDiscount,
  type DiscountBookingFields,
} from "@/lib/rent-discount";

export function TenantDiscountNote({
  booking,
  depositLocked = false,
  showReason = true,
  className = "",
}: {
  booking: DiscountBookingFields | null;
  depositLocked?: boolean;
  /** Set false for dense rows where only the amount matters. */
  showReason?: boolean;
  className?: string;
}) {
  if (!hasRentDiscount(booking) && !hasDepositDiscount(booking)) return null;

  const summary = formatDiscountSummary(booking, { depositLocked });
  if (!summary) return null;

  const reason = booking?.discount_reason?.trim();

  return (
    <div
      className={`rounded-lg border border-amber-200 bg-amber-50/70 px-2.5 py-2 text-[11px] leading-relaxed text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200 ${className}`}
    >
      <div className="flex items-start gap-1.5">
        <BadgePercent className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div className="space-y-0.5">
          <div className="font-medium">{summary}</div>
          {showReason && reason && (
            <div className="text-amber-800/80 dark:text-amber-300/80">
              Reason: “{reason}”
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
